import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';

interface SelectConnectionTypeModalProps {
    isOpen: boolean;
    onClose: () => void;
    onBack?: () => void;
    onSelectChannel: () => void;
    onSelectJournal: () => void;
}

export const SelectConnectionTypeModal: React.FC<SelectConnectionTypeModalProps> = ({
    isOpen,
    onClose,
    onBack,
    onSelectChannel,
    onSelectJournal
}) => {
    const { t } = useTranslation("common");

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-900 border border-transparent dark:border-neutral-800 shadow-xl transition-all">
                                <div className="relative p-6">
                                    <div className="relative flex items-center justify-center mb-6">
                                        {/* Back button */}
                                        {onBack && (
                                            <button
                                                onClick={onBack}
                                                className="absolute left-0 text-neutral-400 hover:text-neutral-500 dark:hover:text-neutral-300 transition-colors focus:outline-none"
                                            >
                                                <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_c6c09ff6.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_c6c09ff6.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                            </button>
                                        )}

                                        <Dialog.Title className="text-xl font-bold text-neutral-900 dark:text-neutral-100 text-center">
                                            {t('boards.addConnectionType', 'Add Connection')}
                                        </Dialog.Title>

                                        <button
                                            onClick={onClose}
                                            className="absolute right-0 text-neutral-400 hover:text-neutral-500 dark:hover:text-neutral-300 transition-colors focus:outline-none"
                                        >
                                            <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_d872171d.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_d872171d.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                        </button>
                                    </div>

                                    <div className="space-y-3">
                                        {/* Channel Option */}
                                        <button
                                            id="onboarding-select-channel-modal"
                                            onClick={() => {
                                                onClose();
                                                onSelectChannel();
                                            }}
                                            className="w-full group bg-white dark:bg-neutral-900 border-2 border-neutral-200 dark:border-neutral-800 rounded-xl p-6 hover:border-accent-primary/50 hover:bg-accent-primary/5 dark:hover:bg-accent-primary/10 transition-all duration-200 text-left"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 bg-accent-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-accent-primary/20 transition-colors">
                                                    <span className="w-6 h-6 text-accent-primary" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_b9f20b8b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_b9f20b8b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
                                                        {t('boards.connectedChannel', 'Connected Channel')}
                                                    </h3>
                                                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                                        {t('boards.connectedChannelDescription', 'Link data from your connected platforms')}
                                                    </p>
                                                </div>
                                                <span className="w-5 h-5 text-neutral-400 flex-shrink-0" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_73d05edf.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_73d05edf.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                            </div>
                                        </button>

                                        {/* Journal Option */}
                                        <button
                                            id="onboarding-add-journal-modal"
                                            onClick={() => {
                                                onClose();
                                                onSelectJournal();
                                            }}
                                            className="w-full group bg-white dark:bg-neutral-900 border-2 border-neutral-200 dark:border-neutral-800 rounded-xl p-6 hover:border-accent-primary/50 hover:bg-accent-primary/5 dark:hover:bg-accent-primary/10 transition-all duration-200 text-left"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 bg-accent-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-accent-primary/20 transition-colors">
                                                    <span className="w-6 h-6 text-accent-primary" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_be35be5f.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_be35be5f.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
                                                        {t('boards.moneyJournal', 'Money Journal')}
                                                    </h3>
                                                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                                        {t('boards.moneyJournalDescription', 'Add operations from your journals')}
                                                    </p>
                                                </div>
                                                <span className="w-5 h-5 text-neutral-400 flex-shrink-0" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_73d05edf.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_73d05edf.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
